import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getTeacherProfile, PublicTeacherProfile } from '../api/teacher-profile';
import { formatMoney } from '../../../commodity/format/money';
import ReviewsSection from '../components/ReviewsSection';
import { getTeacherReviews } from '../api/reviews';

/**
 * S5.7 (TCH-10) — the teacher's public profile.
 *
 * This is the page a parent reaches from a course before deciding whether to
 * trust someone with their child, so it leads with who the teacher is and what
 * they are qualified in, and only then lists what they teach.
 */

const TeacherProfile: React.FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<PublicTeacherProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!teacherId) return;
    setLoading(true);
    getTeacherProfile(teacherId)
      .then((p) => {
        if (active) {
          setProfile(p);
          setError(null);
        }
      })
      .catch((e: any) => {
        if (active) setError(e?.message || 'No pudimos cargar este perfil.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [teacherId]);

  if (loading) return <p>Cargando perfil…</p>;
  if (error || !profile) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={{ color: '#c92a2a' }}>{error || 'Perfil no encontrado.'}</p>
        <button onClick={() => navigate('/courses')} style={linkBtn}>
          Ver todos los cursos
        </button>
      </div>
    );
  }

  const fullName = `${profile.firstName} ${profile.lastName}`.trim();

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <button onClick={() => navigate(-1)} style={linkBtn}>
        &larr; Volver
      </button>

      <header style={headerStyle}>
        <div style={avatarStyle}>{profile.firstName.charAt(0).toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: '0 0 4px', fontSize: 26 }}>{fullName}</h1>
          {profile.verified && (
            <p style={{ margin: '0 0 4px', color: '#2b8a3e', fontSize: 14 }}>Profesor verificado</p>
          )}
          {profile.subjects.length > 0 && (
            <p style={{ margin: 0, color: '#666' }}>{profile.subjects.join(' · ')}</p>
          )}
          {typeof profile.yearsExperience === 'number' && (
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>
              {profile.yearsExperience}{' '}
              {profile.yearsExperience === 1 ? 'año de experiencia' : 'años de experiencia'}
            </p>
          )}
        </div>
      </header>

      {profile.bio && (
        <Section title="Sobre mí">
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{profile.bio}</p>
        </Section>
      )}

      {profile.methodology && (
        <Section title="Cómo enseño">
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{profile.methodology}</p>
        </Section>
      )}

      {(profile.languages.length > 0 ||
        profile.levels.length > 0 ||
        profile.certifications.length > 0) && (
        <Section title="Formación y alcance">
          <dl style={factsStyle}>
            {profile.languages.length > 0 && (
              <Fact label="Idiomas" value={profile.languages.join(', ')} />
            )}
            {profile.levels.length > 0 && (
              <Fact label="Niveles" value={profile.levels.join(', ')} />
            )}
            {profile.certifications.length > 0 && (
              <Fact label="Certificaciones" value={profile.certifications.join(', ')} />
            )}
          </dl>
        </Section>
      )}

      <ReviewsSection
        subject="TEACHER"
        subjectId={teacherId as string}
        load={getTeacherReviews}
        title="Reseñas del profesor"
      />

      {profile.publishedCourses === null ? (
        // PRT-BR-P05-20. The server could not read this teacher's catalog. That
        // is not "nothing published", and saying so would be a claim about the
        // teacher that nobody checked.
        <Section title="Cursos publicados">
          <p role="status" style={{ margin: 0, color: '#666' }}>
            No pudimos cargar los cursos de {profile.firstName} en este momento. Intenta de nuevo
            más tarde.
          </p>
        </Section>
      ) : (
        <Section title={`Cursos publicados (${profile.publishedCourses.length})`}>
          {profile.publishedCourses.length === 0 ? (
            <p style={{ margin: 0, color: '#666' }}>
              {profile.firstName} no tiene cursos publicados en este momento.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
              {profile.publishedCourses.map((c) => (
                <li key={c.courseId}>
                  <button onClick={() => navigate(`/courses/${c.courseId}`)} style={courseRowStyle}>
                    <span style={{ fontWeight: 600 }}>{c.title}</span>
                    <span style={{ color: '#666', fontSize: 13 }}>
                      {c.subject} · {c.duration} min ·{' '}
                      {c.offerMode === 'ONE_ON_ONE' ? 'Individual' : 'Grupo'}
                    </span>
                    <span style={{ fontWeight: 600 }}>{formatMoney(c.price, c.currency)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={sectionStyle}>
    <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{title}</h2>
    {children}
  </section>
);

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <dt style={{ color: '#666', fontSize: 13 }}>{label}</dt>
    <dd style={{ margin: 0, fontSize: 14 }}>{value}</dd>
  </>
);

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#0366d6',
  cursor: 'pointer',
  padding: 0,
  marginBottom: 16,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 20,
  flexWrap: 'wrap',
};

const avatarStyle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #4c6ef5, #1971c2)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 26,
  fontWeight: 700,
  flexShrink: 0,
};

const sectionStyle: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const factsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gap: '6px 16px',
  margin: 0,
};

const courseRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 4,
  width: '100%',
  textAlign: 'left',
  background: '#f8f9fa',
  border: '1px solid #e9ecef',
  borderRadius: 6,
  padding: '10px 12px',
  cursor: 'pointer',
  font: 'inherit',
};

export default TeacherProfile;
